import React, { useContext, useEffect } from 'react'
import { Button, Icon, Toast } from '@mc/design-system'
import { useTranslation, withI18nContext } from '@mc/i18n'
import { SHARE_OPTIONS } from '../../constants'
import { trackShareExternallyClicked } from '../../events'
import openMessageDialog from '../../helpers/openMessageDialog'
import { useClipboard } from '../../hooks'
import i18nInstance from '../../i18n/i18nInstance'
import { openEmailDialog } from '../../lib'
import ShareContext from '../../provider/ShareContext'
import classes from '../ShareModal.module.scss'

type ShareModalButtonsMobileProps = {
  links: {
    email: string
    facebook: string
    messenger: string
    message: string
    twitter: string
    linkedin: string
    copy: string
  }
  email: {
    subject: string
    body: string
  }
  onClose: () => void
}

const ShareModalButtonsMobile = ({
  links,
  email,
  onClose,
}: ShareModalButtonsMobileProps) => {
  const { t } = useTranslation('@mc/share')
  const { copied, onCopy } = useClipboard()
  const shareData = useContext(ShareContext)

  useEffect(() => {
    if (!shareData) {
      return
    }

    if (copied) {
      trackShareExternallyClicked({
        channel: SHARE_OPTIONS.COPY,
        link: links.copy,
        shareData,
      })
    }
  }, [copied, links, shareData])

  const onClickMessageButton = () => {
    if (!shareData) {
      return
    }

    openMessageDialog({
      link: links.message,
      shareData,
    })
    onClose()
  }

  const onClickEmailButton = () => {
    if (!shareData?.course) {
      return
    }

    openEmailDialog({
      link: links.email,
      subject: email.subject,
      body: email.body,
      shareData,
    })
    onClose()
  }

  return (
    <>
      <div className='row mc-mt-5' data-testid='share-modal-mobile-buttons'>
        <div className='col-4'>
          <Button
            kind='tertiary'
            onClick={() => onCopy(links.copy)}
            fullWidth
            data-testid='copy-button'
          >
            <Icon kind='link' srText={t('srOnlyButtons.link')} />
          </Button>
        </div>

        <div className='col-4'>
          <Button
            className={classes.messageButton}
            onClick={onClickMessageButton}
            fullWidth
            data-testid='message-button'
          >
            <Icon kind='message' />
            <span className='mc-sr-only'>{t('srOnlyButtons.message')}</span>
          </Button>
        </div>

        <div className='col-4'>
          <Button
            kind='secondary'
            onClick={onClickEmailButton}
            fullWidth
            data-testid='email-button'
          >
            <Icon kind='mail' srText={t('srOnlyButtons.email')} />
          </Button>
        </div>
      </div>

      <Toast show={copied}>
        <p className='d-flex justify-content-center'>{t('linkCopied')}</p>
      </Toast>
    </>
  )
}

export default withI18nContext(i18nInstance)(ShareModalButtonsMobile)
